import { Button, cn, Typography } from "@suraasa/placebo-ui"
import clsx from "clsx"
import PageContainer from "components/PageContainer"
import PageNavigation from "components/PageNavigation"
import { getRestrictionIcon } from "features/AccessManagement/CheckRestrictions"
import { IMPACT_COURSE_TOUR_TARGETS } from "features/Home/SuraasaPlusNewYearOnboarding/ImpactCourseTour/constants"
import { LearningItem } from "features/LearningItems/Nav"
import { context } from "global/Context/context"
import { Message } from "iconoir-react"
import { useContext } from "react"
import { Link, NavLink, useLocation, useParams } from "react-router-dom"
import routes from "routes"
import { learningItemSlugs } from "utils/constants"
import { getRemoteMedia } from "utils/getRemoteMedia"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { getProductImage } from "../getProductDisplayConfig"
import ImageBackgroundCard from "../ImageBackgroundCard"
import styles from "./Header.module.css"

type Props = {
  className?: string
  // To show Discussions button, defaults to false
  showDiscussionsButton?: boolean
  data: LearningItem[]
  itemSlug: string
  image: string | null
  itemName: string
}

const LearningItemHeader = ({
  image,
  className,
  showDiscussionsButton = false,
  data,
  itemSlug,
  itemName,
}: Props) => {
  const params = useParams()
  const { state } = useLocation()
  const slug: string =
    state?.parentSlug || params.parentSlug || params.slug || state?.slug

  const { learningRestrictions } = useContext(context)

  const { pgctl } = useEnrollments()

  const restrictionType = learningRestrictions?.find(
    i => i.learningItem.slug === slug
  )?.restrictionType

  const titleLogos: Record<string, string> = {
    [learningItemSlugs.med]: `${getRemoteMedia("/logos/itu/default-light.png", {
      width: 192,
    })}`,
  }

  const logo = titleLogos[slug]
  return (
    <ImageBackgroundCard
      disabled={!!restrictionType}
      background={getProductImage({
        defaultImage: image,
        slug: itemSlug,
        deliveryMode: pgctl.deliveryMode,
      })}
      className={clsx(className, "py-2.5 md:py-3")}
    >
      <PageContainer>
        <div className="pb-5 text-common-white-500">
          <PageNavigation color="light" className="mb-1" />
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-1.5">
              <Typography variant="title2" className="!leading-7">
                {itemName}
              </Typography>
              {logo && (
                <>
                  <div className="h-3.5 min-h-full border-l border-white"></div>
                  <img className="w-12" src={logo} alt="" />
                </>
              )}
            </div>
            {showDiscussionsButton && !restrictionType && (
              <Link to={`${routes.discussions}?course=${itemSlug}`}>
                <Button
                  startAdornment={<Message />}
                  variant="text"
                  color="null"
                >
                  Discussions
                </Button>
              </Link>
            )}
            {restrictionType && (
              <div className="flex size-5 items-center justify-center rounded-lg bg-white/10">
                {getRestrictionIcon(restrictionType)}
              </div>
            )}
          </div>
        </div>
      </PageContainer>
      <div id="learning-item-navbar" className={styles.customNav}>
        <PageContainer>
          <div className="flex py-[3px]">
            {data.map((item, index) => (
              <NavLink
                key={index}
                id={
                  item.name() === "Resources"
                    ? "learning-item-navbar-resource"
                    : undefined
                }
                to={item.link}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg px-1.5 py-1 transition-colors  text-white/65 focus:outline-[3px] active:outline-white/10 active:outline me-1.5 shrink-0",
                    {
                      "hover:bg-white/15 hover:text-white": !isActive,
                      "text-onSurface-900 bg-white/90": isActive,
                      [IMPACT_COURSE_TOUR_TARGETS.courseTour.resourceTab]:
                        item.name() === "Resources",
                    }
                  )
                }
              >
                {({ isActive }) => (
                  <Typography variant="subtitle2">
                    {item.name(isActive)}
                  </Typography>
                )}
              </NavLink>
            ))}
          </div>
        </PageContainer>
      </div>
    </ImageBackgroundCard>
  )
}

export default LearningItemHeader
